import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms';
}
export function LegalModal({
  isOpen,
  onClose,
  type
}: LegalModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const privacyContent = {
    title: 'Política de Privacidad',
    content: <div className="space-y-6" data-id="element-104">
        <section data-id="element-105">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-106">
            1. Información que Recopilamos
          </h3>
          <p className="text-gray-700" data-id="element-107">
            Recopilamos información personal que usted nos proporciona
            directamente al realizar una compra, incluyendo nombre, dirección de
            correo electrónico e información de pago. Esta información es
            necesaria para procesar su pedido y entregarle el producto digital.
          </p>
        </section>

        <section data-id="element-108">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-109">
            2. Uso de la Información
          </h3>
          <p className="text-gray-700 mb-2" data-id="element-110">
            Utilizamos su información personal para:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4" data-id="element-111">
            <li data-id="element-112">Procesar y completar su compra</li>
            <li data-id="element-113">Enviarle el producto digital adquirido</li>
            <li data-id="element-114">Proporcionar soporte al cliente</li>
            <li data-id="element-115">Enviar actualizaciones importantes sobre su compra</li>
          </ul>
        </section>

        <section data-id="element-116">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-117">
            3. Protección de Datos
          </h3>
          <p className="text-gray-700" data-id="element-118">
            Implementamos medidas de seguridad técnicas y organizativas para
            proteger su información personal contra acceso no autorizado,
            alteración, divulgación o destrucción. Todos los pagos son
            procesados a través de plataformas seguras que cumplen con los
            estándares PCI-DSS.
          </p>
        </section>

        <section data-id="element-119">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-120">
            4. Compartir Información
          </h3>
          <p className="text-gray-700" data-id="element-121">
            No vendemos, intercambiamos ni transferimos su información personal
            a terceros, excepto a proveedores de servicios de confianza que nos
            ayudan a operar nuestro sitio web y procesar pagos, siempre que
            dichas partes acuerden mantener esta información confidencial.
          </p>
        </section>

        <section data-id="element-122">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-123">
            5. Sus Derechos
          </h3>
          <p className="text-gray-700" data-id="element-124">
            Usted tiene derecho a acceder, corregir o eliminar su información
            personal en cualquier momento. Para ejercer estos derechos, puede
            contactarnos directamente.
          </p>
        </section>

        <section data-id="element-125">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-126">
            6. Cookies
          </h3>
          <p className="text-gray-700" data-id="element-127">
            Utilizamos cookies esenciales para el funcionamiento del sitio web.
            No utilizamos cookies de seguimiento o publicidad de terceros.
          </p>
        </section>

        <section data-id="element-128">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-129">
            7. Cambios a esta Política
          </h3>
          <p className="text-gray-700" data-id="element-130">
            Nos reservamos el derecho de actualizar esta política de privacidad
            en cualquier momento. Los cambios entrarán en vigor inmediatamente
            después de su publicación en esta página.
          </p>
        </section>
      </div>
  };
  const termsContent = {
    title: 'Términos y Condiciones',
    content: <div className="space-y-6" data-id="element-131">
        <section data-id="element-132">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-133">
            1. Aceptación de Términos
          </h3>
          <p className="text-gray-700" data-id="element-134">
            Al realizar una compra de "Sinergia Ancestral", usted acepta estos
            términos y condiciones en su totalidad. Si no está de acuerdo con
            alguna parte de estos términos, no debe realizar la compra.
          </p>
        </section>

        <section data-id="element-135">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-136">
            2. Descripción del Producto
          </h3>
          <p className="text-gray-700" data-id="element-137">
            "Sinergia Ancestral" es un producto digital (ebook en formato PDF)
            de carácter educativo e informativo sobre salud natural y
            fitoterapia. El producto incluye 20 páginas de contenido, 6
            protocolos completos, guía técnica de preparación y calendario de
            transformación de 30 días.
          </p>
        </section>

        <section data-id="element-138">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-139">
            3. Descargo de Responsabilidad Médica
          </h3>
          <p className="text-gray-700 mb-2" data-id="element-140">
            <strong data-id="element-141">IMPORTANTE:</strong> Este producto es de carácter educativo
            e informativo únicamente. No está destinado a diagnosticar, tratar,
            curar ni prevenir ninguna enfermedad.
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4" data-id="element-142">
            <li data-id="element-143">
              Consulte siempre a su profesional de salud antes de iniciar
              cualquier protocolo natural
            </li>
            <li data-id="element-144">Especialmente importante si toma medicamentos recetados</li>
            <li data-id="element-145">
              No suspenda ningún tratamiento médico sin consultar a su médico
            </li>
            <li data-id="element-146">Los resultados individuales pueden variar</li>
          </ul>
        </section>

        <section data-id="element-147">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-148">
            4. Entrega del Producto
          </h3>
          <p className="text-gray-700" data-id="element-149">
            El producto digital será entregado por correo electrónico
            inmediatamente después de confirmar el pago. Usted recibirá un
            enlace de descarga válido para acceder al archivo PDF. Es su
            responsabilidad descargar y guardar el archivo en un plazo
            razonable.
          </p>
        </section>

        <section data-id="element-150">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-151">
            5. Garantía de Devolución de 60 Días
          </h3>
          <p className="text-gray-700" data-id="element-152">
            Ofrecemos una garantía de devolución de dinero de 60 días sin
            preguntas. Si no está satisfecho con el producto por cualquier
            razón, puede solicitar un reembolso completo dentro de los 60 días
            posteriores a la compra contactándonos directamente.
          </p>
        </section>

        <section data-id="element-153">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-154">
            6. Propiedad Intelectual
          </h3>
          <p className="text-gray-700" data-id="element-155">
            Todo el contenido de "Sinergia Ancestral", incluyendo texto,
            gráficos, imágenes y diseño, está protegido por derechos de autor.
            La compra le otorga una licencia personal, no transferible, para uso
            individual. No está permitido:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4 mt-2" data-id="element-156">
            <li data-id="element-157">Reproducir, distribuir o vender el contenido</li>
            <li data-id="element-158">Compartir el archivo con terceros</li>
            <li data-id="element-159">Publicar el contenido en línea</li>
            <li data-id="element-160">Modificar o crear obras derivadas</li>
          </ul>
        </section>

        <section data-id="element-161">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-162">
            7. Limitación de Responsabilidad
          </h3>
          <p className="text-gray-700" data-id="element-163">
            En la máxima medida permitida por la ley, no seremos responsables de
            ningún daño directo, indirecto, incidental, especial o consecuente
            que resulte del uso o la imposibilidad de usar este producto.
          </p>
        </section>

        <section data-id="element-164">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-165">
            8. Modificaciones
          </h3>
          <p className="text-gray-700" data-id="element-166">
            Nos reservamos el derecho de modificar estos términos en cualquier
            momento. Los cambios entrarán en vigor inmediatamente después de su
            publicación.
          </p>
        </section>

        <section data-id="element-167">
          <h3 className="font-heading text-xl text-gray-900 mb-3" data-id="element-168">
            9. Ley Aplicable
          </h3>
          <p className="text-gray-700" data-id="element-169">
            Estos términos se rigen por las leyes aplicables en su jurisdicción.
            Cualquier disputa será resuelta mediante arbitraje o en los
            tribunales competentes.
          </p>
        </section>
      </div>
  };
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <AnimatePresence data-id="element-170">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm" aria-hidden="true" data-id="element-171" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-172">
            <div className="flex min-h-full items-center justify-center p-4" data-id="element-173">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} transition={{
            duration: 0.2
          }} className="relative bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-174">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b-2 border-sage-light px-6 py-4 flex items-center justify-between z-10" data-id="element-175">
                  <h2 id="modal-title" className="font-heading text-2xl md:text-3xl text-warm-brown" data-id="element-176">
                    {content.title}
                  </h2>
                  <button onClick={onClose} className="p-2 rounded-lg hover:bg-sage-light/30 transition-colors" aria-label="Cerrar modal" data-id="element-177">
                    <XIcon className="w-6 h-6 text-gray-600" data-id="element-178" />
                  </button>
                </div>

                {/* Content */}
                <div className="px-6 py-6 overflow-y-auto max-h-[calc(85vh-80px)]" data-id="element-179">
                  {content.content}
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent px-6 py-4 border-t border-sage-light" data-id="element-180">
                  <button onClick={onClose} className="w-full bg-sage hover:bg-sage-dark text-white font-semibold py-3 px-6 rounded-lg transition-colors" data-id="element-181">
                    Cerrar
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </>}
    </AnimatePresence>;
}